import type { SyntheticEvent } from 'react';
import React from 'react';
import styled from 'styled-components';
import { Checkbox as SCheckbox } from 'semantic-ui-react';
import { SchemaTypes } from '@axiom/types';

import { TilesItem } from '../../layout/Tiles/TilesItem';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

import { RawCheckboxPill } from './RawCheckboxPill';

const CheckboxWrapper = styled.div``;

export type RawCheckboxOptionType = string | number | boolean;
export type RawCheckboxValueType =
  string | number | boolean | null | Array<string | number | boolean | null>;

const ALLOWED_TYPES = [
  SchemaTypes.ZodArray,
  SchemaTypes.ZodString,
  SchemaTypes.ZodNumber,
  SchemaTypes.ZodBoolean,
] as const;

const formatters = {
  [SchemaTypes.ZodArray]: {
    in: (value: RawCheckboxValueType, option: RawCheckboxOptionType) =>
      Array.isArray(value) && value.includes(option),
    out: (
      _isChecked: boolean,
      option: RawCheckboxOptionType,
      originalValue?: RawCheckboxValueType
    ) => {
      if (!Array.isArray(originalValue)) {
        originalValue = [];
      }

      return originalValue.includes(option)
        ? originalValue.filter(v => v !== option)
        : [...originalValue, option];
    },
  },
  [SchemaTypes.ZodString]: {
    in: (value: RawCheckboxValueType) => value !== null,
    out: (isChecked: boolean, option: RawCheckboxOptionType) =>
      isChecked ? option : null,
  },
  [SchemaTypes.ZodNumber]: {
    in: (value?: RawCheckboxValueType) => value !== null,
    out: (isChecked: boolean, option: RawCheckboxOptionType) =>
      isChecked ? option : null,
  },
  [SchemaTypes.ZodBoolean]: {
    in: (value?: RawCheckboxValueType) => value === true,
    out: (isChecked: boolean) => !!isChecked,
  },
};

export type RawCheckboxProps = {
  dataType: (typeof ALLOWED_TYPES)[number];
  disabled?: boolean;
  displayValue?: string | React.ReactNode;
  id?: string;
  invalid?: boolean;
  mode?: 'checkbox' | 'tile' | 'pill';
  name: string;
  onChange?: (
    value: RawCheckboxValueType,
    e: SyntheticEvent
  ) => Promise<unknown> | void;
  option: RawCheckboxOptionType;
  value?: RawCheckboxValueType;
};

export const RawCheckbox = ({
  dataType,
  disabled = false,
  displayValue = null,
  id,
  invalid = false,
  mode = 'checkbox',
  name,
  onChange,
  option,
  value = null,
}: RawCheckboxProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (formatters[dataType] === undefined) {
    throw new Error(`Unknown Schema Type: ${dataType}`);
  }
  const isChecked = formatters[dataType].in(value, option);
  const handleCheckClick = (e: SyntheticEvent) => {
    const checkedValue = formatters[dataType].out(!isChecked, option, value);
    void onChange?.(checkedValue, e);
  };
  const displayVal =
    displayValue && typeof displayValue !== 'string' ? (
      <label>{displayValue}</label>
    ) : (
      displayValue
    );

  const CheckboxChunk = (
    <SCheckbox
      className={AttrsHelper.formatClassname(invalid && 'invalid')}
      checked={isChecked}
      disabled={disabled}
      id={id}
      label={displayVal}
      name={name}
      onChange={handleCheckClick}
    />
  );

  switch (mode) {
    case 'tile': {
      return (
        <TilesItem
          name={name}
          disabled={disabled}
          onClick={handleCheckClick}
          selected={isChecked}
        >
          {CheckboxChunk}
        </TilesItem>
      );
    }
    case 'pill': {
      return (
        <RawCheckboxPill
          name={name}
          disabled={disabled}
          onClick={handleCheckClick}
          selected={isChecked}
          invalid={invalid}
        >
          {CheckboxChunk}
        </RawCheckboxPill>
      );
    }
    default: {
      return (
        <CheckboxWrapper data-test={name}>{CheckboxChunk}</CheckboxWrapper>
      );
    }
  }
};
